import type { CalcOutput, ChatTranscriptMessage, ChatUIMessage, SseEvent } from './types'
import { readAttribution } from './attribution'
import { readLocale } from './locale'
import type { Lang } from '../i18n'

// In dev the Vite proxy forwards `/api/*` to the Worker at 127.0.0.1:8787, so
// API_BASE must stay empty even though the app is served under /tdee/. In prod
// the app lives at nanorhino.com/tdee so API calls must be prefixed accordingly.
const API_BASE = import.meta.env.DEV ? '' : import.meta.env.BASE_URL.replace(/\/$/, '')

const CONVERSATION_ID_KEY = 'tdee_conversation_id'

// In-memory copy of the conversation id, used as the PRIMARY source. Android
// in-app WebViews (Reddit/FB/IG/ChatGPT, etc.) frequently block or wipe
// localStorage between requests, so a localStorage-only id silently failed to
// persist — every turn sent no id, the server minted a fresh UUID, and a single
// interview fragmented into one conversation row per turn (admin showed it as
// many sessions; funnel + handoff summary/language detection broke). The module
// variable lives for the whole SPA page session regardless of storage, so the
// id now stays stable across turns even when localStorage throws.
let inMemoryConversationId: string | null = null

function readConversationId(): string | null {
  if (inMemoryConversationId) return inMemoryConversationId
  try {
    return localStorage.getItem(CONVERSATION_ID_KEY)
  } catch {
    return null
  }
}

export function writeConversationId(id: string): void {
  inMemoryConversationId = id
  try {
    localStorage.setItem(CONVERSATION_ID_KEY, id)
  } catch {
    // localStorage blocked (private mode, quota, in-app WebView) — the
    // in-memory copy above keeps the id alive for the rest of the session.
  }
}

// Drop both copies so the next chat turn starts a brand-new conversation
// (used by "Recalculate"). Clearing only localStorage would leave the stale
// in-memory id in place and silently resume the old conversation.
export function clearConversationId(): void {
  inMemoryConversationId = null
  try {
    localStorage.removeItem(CONVERSATION_ID_KEY)
  } catch {
    // ignore (private mode, quota, etc.)
  }
}

// Under-18 guardrail (browser-local mirror of the server-side block). Once the
// server flags the user as a minor, this sticks in the same browser so the
// calculator stays locked even across "Recalculate" (which mints a fresh
// conversation id) and page reloads. Same in-memory + localStorage dual-store
// as the conversation id, for the same reason: in-app WebViews wipe
// localStorage between requests, so the in-memory copy keeps the block alive
// for the page session. Deliberately NOT cleared by clearConversationId — the
// only intended path back is emailing support@nanorhino.com (support clears it
// case-by-case; clearing browser data also resets it, which we don't advertise).
const MINOR_BLOCKED_KEY = 'tdee_minor_blocked'
let inMemoryMinorBlocked = false

export function isMinorBlockedLocal(): boolean {
  if (inMemoryMinorBlocked) return true
  try {
    return localStorage.getItem(MINOR_BLOCKED_KEY) === '1'
  } catch {
    return false
  }
}

export function setMinorBlockedLocal(): void {
  inMemoryMinorBlocked = true
  try {
    localStorage.setItem(MINOR_BLOCKED_KEY, '1')
  } catch {
    // localStorage blocked (private mode, quota, in-app WebView) — the
    // in-memory copy above keeps the block alive for the rest of the session.
  }
}

export interface StreamChatParams {
  messages: ChatUIMessage[]
  // Landing-page context. 'glp1' (set by the /tdee/glp1-calculator page) nudges
  // the server interview to record the glp1 medication so on_glp1 flows
  // downstream. Omitted on the default /tdee page.
  landing?: 'glp1'
  // UI locale from the URL path (en default, es on /tdee/es/). Runs the
  // interview + every deterministic server string in this language.
  lang?: Lang
  signal?: AbortSignal
  onEvent: (e: SseEvent) => void
}

export async function streamChat({ messages, landing, lang, signal, onEvent }: StreamChatParams): Promise<void> {
  const conversationId = readConversationId()
  // Cards are client-side rendering state; the server only knows about
  // text-shaped messages. Strip the rest before sending.
  const wireMessages: ChatTranscriptMessage[] = messages.flatMap((m) =>
    m.kind === 'text' ? [{ role: m.role, content: m.content }] : []
  )
  const res = await fetch(`${API_BASE}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      messages: wireMessages,
      ...(landing ? { landing } : {}),
      ...(lang && lang !== 'en' ? { lang } : {}),
      ...(conversationId ? { conversation_id: conversationId } : {}),
      ...(() => {
        const attribution = readAttribution()
        return attribution ? { attribution } : {}
      })(),
      ...(() => {
        const locale = readLocale()
        return locale ? { locale } : {}
      })(),
    }),
    signal,
  })
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '')
    throw new Error(`chat request failed: ${res.status} ${text}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const parsed = parseSseChunk(chunk)
      if (!parsed) continue
      if (parsed.event === 'session') {
        writeConversationId(parsed.data.conversation_id)
      } else if (parsed.event === 'minor_blocked') {
        // Persist centrally (like writeConversationId) so the block survives
        // even if a caller's onEvent handler doesn't act on it.
        setMinorBlockedLocal()
      }
      onEvent(parsed)
    }
  }
}

// Streams a post-result on-page generation (plan or meal day) over SSE. Uses
// the same wire format as /api/chat — `event: text` for deltas, `done` to
// terminate, `error` on failure — so the caller can mirror streamChat logic.
//
// Cached generations replay as a single `text` event followed by `done`, so
// the consumer does not need to special-case them.
export interface StreamPlanParams {
  conversationId: string
  kind: 'plan' | 'meal'
  // Plan-iteration request — present when the user asked to revise an existing
  // plan ("make it vegetarian"). The server bypasses its cache and feeds this
  // into the prompt builder. Only meaningful for kind='plan'.
  modification?: string | null
  // UI locale (en default, es on /tdee/es/) — produces the plan / meal in
  // this language.
  lang?: Lang
  signal?: AbortSignal
  onText: (delta: string) => void
  onDone?: (stopReason: string) => void
  onError?: (message: string) => void
}

export async function streamPlan({
  conversationId,
  kind,
  modification,
  lang,
  signal,
  onText,
  onDone,
  onError,
}: StreamPlanParams): Promise<void> {
  const path = kind === 'plan' ? '/api/plan/generate' : '/api/plan/meals'
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      conversation_id: conversationId,
      ...(modification ? { modification } : {}),
      ...(lang && lang !== 'en' ? { lang } : {}),
    }),
    signal,
  })
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '')
    throw new Error(`plan request failed: ${res.status} ${text}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const parsed = parseSseChunk(chunk)
      if (!parsed) continue
      if (parsed.event === 'text') {
        onText(parsed.data.delta)
      } else if (parsed.event === 'done') {
        onDone?.(parsed.data.stop_reason)
      } else if (parsed.event === 'error') {
        onError?.(parsed.data.message)
      }
    }
  }
}

export type EmailCaptureSource = 'us_fallback' | 'intl_form'

export async function submitPlanEmail(
  conversationId: string,
  email: string,
  source?: EmailCaptureSource
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/plan/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      source
        ? { conversation_id: conversationId, email, source }
        : { conversation_id: conversationId, email }
    ),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || `status ${res.status}`)
  }
}

// Hydrate the SPA from a stored session UUID. Returns the full transcript +
// last TDEE result (with the CTA-card extras the server threads onto it —
// handoff_token / country / intent / on_glp1 / has_existing_handoff, same
// fields as a live result_payload). Two callers: the /tdee/sessions/<uuid>
// debug surface, and resume-on-reload (App.tsx calls it on mount with the
// persisted conversation id so a reload restores the conversation instead of
// blanking it).
//
// Returns null on 404 — the conversation no longer exists server-side, and
// the resume path uses that to drop the stale local id and start fresh.
export interface LoadedSession {
  conversation_id: string
  // Plain transcript shape — lib/resume.ts hydrates into ChatUIMessage[].
  messages: ChatTranscriptMessage[]
  result: CalcOutput | null
}

export async function loadSession(sessionId: string): Promise<LoadedSession | null> {
  const res = await fetch(`${API_BASE}/api/sessions/${encodeURIComponent(sessionId)}`)
  if (res.status === 404) return null
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || `status ${res.status}`)
  }
  return (await res.json()) as LoadedSession
}

export type ChannelChoice = 'sms' | 'whatsapp' | 'imessage' | 'email' | 'other'

export async function submitChannelChoice(
  conversationId: string,
  channel: ChannelChoice
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/plan/channel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversation_id: conversationId, channel }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || `status ${res.status}`)
  }
}

function parseSseChunk(raw: string): SseEvent | null {
  let event: string | null = null
  const dataLines: string[] = []
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (!event || dataLines.length === 0) return null
  try {
    const data = JSON.parse(dataLines.join('\n'))
    return { event, data } as SseEvent
  } catch {
    return null
  }
}

